import { AccessDenied, Alert, AppShell, Button, pillClass, Spinner, ToastProvider } from '@rooming/ui';
import { appNameFor } from '@rooming/web-core';
import { useState } from 'react';
import { BrowserRouter, Navigate, NavLink, Route, Routes } from 'react-router';
import { AuditPage, DashboardPage, HelpPage, UsersPage } from './pages/AdminPages';
import { GroupsPage, HotelsPage } from './pages/CatalogPages';
import { ExternalStayPage, ExternalStaysPage } from './pages/ExternalPages';
import { GroupPage } from './pages/GroupPage';
import { PlacementBadge, PlacementRequestsPage } from './pages/PlacementPages';
import { LoginPage, OwnPasswordPage, SecondFactorPage } from './pages/LoginPage';
import { ExecutiveAuditPage } from './pages/ExecutiveAuditPage';
import { NoticesLink, NoticesPage } from './pages/NoticesPage';
import { SessionProvider, useSession } from './session';

// Navigation of BACKOFFICE_DESIGN §2. The backoffice is the administrator's (the one internal role, prompt12 §11).
// "Notificaciones" is already in the header (with its unread count) for every account. The menu follows the order of
// the work: hotels → groups → users; "Ayuda" explains the whole process. School and contract are typed in the group
// form itself (prompt21): there is no separate page for them.
const EXTERNAL_NAV: readonly { to: string; text: string }[] = [{ to: '/', text: 'Mis estadías' }];
const NAV: readonly { to: string; text: string }[] = [
  { to: '/', text: 'Dashboard' },
  { to: '/hoteles', text: 'Hoteles' },
  { to: '/grupos', text: 'Grupos' },
  { to: '/solicitudes', text: 'Solicitudes de plaza' },
  { to: '/usuarios', text: 'Usuarios' },
  { to: '/auditoria', text: 'Auditoría' },
  { to: '/auditoria-ejecutiva', text: 'Auditoría ejecutiva' },
  { to: '/ayuda', text: 'Ayuda' },
];

/** What a hotel administrator never sees in the menu. */
const SUPER_ONLY: ReadonlySet<string> = new Set(['/usuarios', '/auditoria', '/auditoria-ejecutiva']);

/** The main menu (administrator, or hotel and guide). */
function MainNav({ items }: { readonly items: readonly { to: string; text: string }[] }) {
  return (
    <nav aria-label="Principal" className="mb-6 flex flex-wrap gap-2">
      {items.map((item) => (
        <NavLink key={item.to} to={item.to} end={item.to === '/'} className={({ isActive }) => pillClass(isActive)}>
          {item.text}
          {item.to === '/solicitudes' && <PlacementBadge />}
        </NavLink>
      ))}
    </nav>
  );
}

function Screens() {
  const { loading, stage, me, signOut, hasRole } = useSession();
  const [step, setStep] = useState<'MFA_ENROLL' | 'MFA_VERIFY'>();
  const exit = (): void => void signOut();
  const active = stage === 'ACTIVE' && me !== undefined;
  const staff = active && me.kind === 'STAFF';
  const external = active && me.kind === 'EXTERNAL';

  return (
    <AppShell
      appName={appNameFor('Funtour · Rooming List', window.location.hostname)}
      wide
      actions={
        active ? (
          <>
            {(staff || external) && <NoticesLink />}
            <span className="text-sm text-muted">{me.displayName}</span>
            <Button variant="ghost" onClick={exit}>
              Salir
            </Button>
          </>
        ) : undefined
      }
    >
      {loading ? (
        <Spinner />
      ) : stage === 'ANONYMOUS' ? (
        <LoginPage onNext={setStep} />
      ) : stage === 'CREDENTIAL_CHANGE_PENDING' ? (
        <OwnPasswordPage />
      ) : stage !== 'ACTIVE' ? (
        <SecondFactorPage step={step} />
      ) : staff && !hasRole('TENANT_ADMIN', 'HOTEL_ADMIN') ? (
        // A staff account that only holds a legacy role (or none): the API refuses it everything.
        <Alert tone="warning" title="Tu cuenta no tiene el rol de administrador">
          Pedile a un administrador que te lo asigne en Usuarios.
          <Button variant="secondary" className="mt-3" onClick={exit}>
            Salir
          </Button>
        </Alert>
      ) : staff ? (
        <>
          <MainNav items={hasRole('TENANT_ADMIN') ? NAV : NAV.filter((item) => !SUPER_ONLY.has(item.to))} />
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/grupos" element={<GroupsPage />} />
            <Route path="/grupos/:groupId/*" element={<GroupPage />} />
            <Route path="/hoteles" element={<HotelsPage />} />
            <Route path="/solicitudes" element={<PlacementRequestsPage />} />
            {/* Users and the audit are the super administrator's (ADR-0013): a hotel administrator goes back to the start. */}
            <Route path="/usuarios" element={hasRole('TENANT_ADMIN') ? <UsersPage /> : <Navigate to="/" replace />} />
            <Route path="/auditoria" element={hasRole('TENANT_ADMIN') ? <AuditPage /> : <Navigate to="/" replace />} />
            <Route
              path="/auditoria-ejecutiva"
              element={hasRole('TENANT_ADMIN') ? <ExecutiveAuditPage /> : <Navigate to="/" replace />}
            />
            <Route path="/ayuda" element={<HelpPage />} />
            <Route path="/notificaciones" element={<NoticesPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </>
      ) : external ? (
        <>
          {/* Hotel and guide (owner, 2026-10-07): a visible way back to their stays from any page, like the staff menu. */}
          <MainNav items={EXTERNAL_NAV} />
          <Routes>
            <Route path="/" element={<ExternalStaysPage />} />
            <Route path="/estadias/:stayId" element={<ExternalStayPage />} />
            <Route path="/notificaciones" element={<NoticesPage />} />
            {/* Any other address (the administrator's pages included) is not for a hotel or a guide. */}
            <Route
              path="*"
              element={
                <AccessDenied>
                  <Navigate to="/" replace />
                </AccessDenied>
              }
            />
          </Routes>
        </>
      ) : (
        // A passenger or guardian session has nothing to do here: their app is a different one.
        <Alert tone="warning" title="Esta aplicación es para el personal, hoteles y guías">
          <Button variant="secondary" className="mt-3" onClick={exit}>
            Salir
          </Button>
        </Alert>
      )}
    </AppShell>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <SessionProvider>
          <Screens />
        </SessionProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}
